import {
  IFileAndCaption,
  IGroupInfo,
  IGroupInfoMembers,
  IMessage,
  ImmutableChat,
  searchUsersByName,
  USER_STATUS,
  USER_TYPE,
} from "@home-health-notify/domain";
import MedicationLiquidOutlinedIcon from "@mui/icons-material/MedicationLiquidOutlined";
import SickOutlinedIcon from "@mui/icons-material/SickOutlined";
import Box from "@mui/material/Box";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Mention, MentionsInput } from "react-mentions";
import DisplayAvatarImage from "../../common/DisplayAvatarImage";
import { AVATAR_SIZE } from "../../utils/AvatarUtils";
import { mentionInputClassName } from "../MessagePanel.style";

interface MentionInputBoxProps {
  chat: ImmutableChat;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | unknown,
    newValue: string,
    newPlainTextValue: string,
  ) => void;
  chatMessage: string;
  send: () => Promise<void>;
  mentionedUsers: string[];
  disableMention?: boolean;
  replyMessage?: IMessage;
  onPasteFile: (fileList: IFileAndCaption[]) => Promise<void>;
}

const MentionInputBox = ({
  chat,
  onAdd,
  onRemove,
  onChange,
  chatMessage,
  send,
  mentionedUsers,
  disableMention = false,
  replyMessage,
  onPasteFile,
}: MentionInputBoxProps) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const currentOrganization = selectedOrgUser?.getOrganization();
  const members: IGroupInfoMembers[] =
    (chat.chatInfo as IGroupInfo)?.members ?? [];

  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const inputMessageRef = useRef<HTMLTextAreaElement>(null);
  const lastValueRef = useRef<string>(chatMessage);
  const allUsers = !disableMention
    ? members
        ?.filter(
          member =>
            currentOrganization &&
            member.userId !== currentOrganization.userProfile.id &&
            userDirectory.getUserInfo(member.userId).status ===
              USER_STATUS.ACTIVE,
        )
        .map((member: IGroupInfoMembers) => {
          const user = userDirectory.getUserInfo(member.userId);
          return {
            id: member.userId,
            display: user.title,
            member,
          };
        })
        .sort((a, b) => a.display.localeCompare(b.display))
    : [];

  const searchUsers = (query: string, callback: (data: any[]) => void) => {
    if (!query) {
      callback(allUsers);
      return;
    }

    const userInfoList = allUsers.map(user => ({
      ...userDirectory.getUserInfo(user.id),
    }));

    const filteredUsers = searchUsersByName(query, userInfoList);
    const results = filteredUsers.map(userInfo => ({
      id: userInfo.id,
      display: userInfo.title,
      member: members.find(
        (member: IGroupInfoMembers) => member.userId === userInfo.id,
      ),
    }));

    callback(results);
  };

  useEffect(() => {
    if (
      inputMessageRef.current &&
      typeof inputMessageRef.current.focus === "function"
    ) {
      inputMessageRef.current.focus();
    }
  }, [replyMessage]);

  // Keep lastValueRef in sync with chatMessage prop
  useEffect(() => {
    lastValueRef.current = chatMessage;
  }, [chatMessage]);

  const handleChange = useCallback(
    (
      event:
        | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
        | unknown,
      newValue: string,
      newPlainTextValue: string,
      mentions: any[],
    ) => {
      onChange(event, newValue, newPlainTextValue);
      const mentionedUsersData: IGroupInfoMembers[] = mentionedUsers
        .map(userId =>
          (chat.chatInfo as IGroupInfo)?.members?.find(
            (member: IGroupInfoMembers) => member.userId === userId,
          ),
        )
        .filter((user): user is IGroupInfoMembers => !!user);

      const removedMentions = mentionedUsersData.filter(user => {
        const userInfo = userDirectory.getUserInfo(user.userId);

        return !mentions.some(mention => {
          return (
            String(mention.id) === user.userId ||
            mention.id === userInfo.title ||
            mention.display === userInfo.title
          );
        });
      });
      const removedMentionIds = removedMentions.map(mention => mention.userId);

      removedMentionIds.forEach(onRemove);
    },
    [onChange, mentionedUsers, chat.chatInfo, userDirectory, onRemove],
  );

  // // Handle spell check corrections and other input events
  // useEffect(() => {
  //   const inputElement = inputMessageRef.current;
  //   if (!inputElement) return;

  //   const handleInput = (event: Event) => {
  //     const target = event.target as HTMLTextAreaElement;
  //     const currentValue = target.value;

  //     // Only process if the value actually changed
  //     if (currentValue !== lastValueRef.current) {
  //       lastValueRef.current = currentValue;
  //       // Manually trigger onChange with the corrected value
  //       handleChange(event, currentValue, currentValue, []);
  //     }
  //   };

  //   inputElement.addEventListener('input', handleInput);
  //   return () => {
  //     inputElement.removeEventListener('input', handleInput);
  //   };
  // }, [handleChange]);

  const defaultRenderSuggestionItem = (suggestion: {
    id: string;
    display?: string;
  }) => {
    const userInfo = userDirectory.getUserInfo(suggestion.id);
    const itemContent = suggestion.display;
    const userType = userInfo.type;

    return (
      <Box
        component="div"
        data-testid="suggestion-item"
        sx={{
          alignItems: "center",
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          height: "100%",
          width: "100%",
          px: 2,
          py: 0.75,
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <DisplayAvatarImage
          avatarSize={AVATAR_SIZE.XSMALL}
          src={userInfo.image}
          alt={userInfo.title}
          onlyFirstNameInitial
        />

        <Box sx={{ ml: 1 }}>{itemContent}</Box>

        {userType === USER_TYPE.PATIENT && (
          <SickOutlinedIcon sx={{ ml: 1 }} color="primary" fontSize="small" />
        )}

        {userType === USER_TYPE.PHYSICIAN && (
          <MedicationLiquidOutlinedIcon
            sx={{ ml: 1 }}
            color="primary"
            fontSize="small"
          />
        )}
      </Box>
    );
  };

  const keyPress = async (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      // Don't generate a new line
      e.preventDefault();
      await send();
    }
  };

  const onPaste = (
    event: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const pastedTypes = event.clipboardData.types;

    if (pastedTypes.includes("Files")) {
      const fileList = event.clipboardData.files;
      const pasteIncludesText = pastedTypes.includes("text/plain");
      if (!!pasteIncludesText && fileList.length > 1) {
        throw new Error(
          "Unexpected: multiple file paste with just a single caption!",
        );
      }

      const pastedText = event.clipboardData.getData("text/plain");
      const pastedFiles: IFileAndCaption[] = [];
      for (let index = 0; index < fileList.length; index++) {
        const file: File = fileList[index];
        pastedFiles.push({
          file,
          caption: pasteIncludesText ? pastedText : file.name,
        });
      }

      event.preventDefault();
      (
        event as unknown as SyntheticEvent
      ).nativeEvent.stopImmediatePropagation();

      onPasteFile(pastedFiles);
    }
  };

  return (
    <MentionsInput
      autoFocus
      onPaste={onPaste}
      value={chatMessage}
      allowSuggestionsAboveCursor={true}
      forceSuggestionsAboveCursor={true}
      onChange={handleChange}
      onKeyDown={keyPress}
      className={mentionInputClassName}
      placeholder={tNs("placeHolder.message") + " " + chat.chatInfo.name}
      inputRef={inputMessageRef}
      spellCheck="true"
      data-testid={"mentions-input"}
    >
      <Mention
        markup="@[__display__]"
        trigger="@"
        displayTransform={(id: string, display: string) => `@${display}`}
        data={searchUsers}
        appendSpaceOnAdd={true}
        onAdd={id => onAdd("" + id)}
        renderSuggestion={suggestion =>
          defaultRenderSuggestionItem({
            id: "" + suggestion.id,
            display: suggestion.display,
          })
        }
      />
    </MentionsInput>
  );
};

export default MentionInputBox;
